import { useCallback, useMemo, useState } from "react";
import { SiteReportLoadingState } from "./SiteReportContent";
import { AssessmentTab } from "./types";

export enum TabLoadingState {
  LOADING = "loading",
  ERROR = "error",
  READY = "ready",
}

export function useReportLoadingState() {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadingState, setReportLoadingState] = useState<SiteReportLoadingState>({});

  const getTabLoadingState = useCallback(
    (tab: AssessmentTab, getCheckboxState: (path: string) => boolean) => {
      let hasLoadingLayers = false;
      let hasLayersWithErrors = false;

      const layerIds = tab.groups.flatMap((group) =>
        group.sectionIds
          ? group.sectionIds.filter((sectionId) => getCheckboxState(`${tab.key}.${group.id}.${sectionId}`))
          : group.layerIds.filter((layerId) => getCheckboxState(`${tab.key}.${layerId}`))
      );

      for (const layerId of layerIds) {
        const layerState = loadingState[layerId];

        if (layerState?.loading) {
          hasLoadingLayers = true;
        }

        if (layerState?.error) {
          hasLayersWithErrors = true;
        }
      }

      if (hasLoadingLayers) {
        return TabLoadingState.LOADING;
      }
      if (hasLayersWithErrors) {
        return TabLoadingState.ERROR;
      }
      return TabLoadingState.READY;
    },
    [loadingState]
  );

  const getLayerLoadingState = useCallback(
    (id: string) => {
      return loadingState[id];
    },
    [loadingState]
  );

  const isReadyToDownload = useMemo(() => {
    return Object.values(loadingState).every(
      (layerLoadingState) => !layerLoadingState.mounted || !layerLoadingState.loading
    );
  }, [loadingState]);

  return {
    hasLoaded,
    isReadyToDownload,
    setHasLoaded,
    getTabLoadingState,
    getLayerLoadingState,
    setReportLoadingState,
  };
}
